/*
 *   Copyright 2022-2025 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import * as echarts from "echarts";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import { ColorLib } from "../../utils/color-lib";
export class DiscoveryPieComponent {
    constructor() {
        this.options = new Param();
        this.debug = false;
        this.parsing = false;
        this.rendering = false;
        this.defOptions = new Param();
        this.divider = 1000;
        this.innerWidth = 0;
        this.innerHeight = 0;
    }
    updateType(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.chartOpts = this.convert(GTSLib.getData(this.result));
            this.setOpts(true);
        }
    }
    updateRes() {
        this.chartOpts = this.convert(GTSLib.getData(this.result));
        this.setOpts(true);
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!Utils.deepEqual(opts, this.innerOptions)) {
            this.innerOptions = Utils.clone(opts);
            if (!!this.myChart) {
                this.chartOpts = this.convert(this.result || new DataModel());
                this.setOpts(true);
            }
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue }, this.chartOpts);
        }
    }
    async resize() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        const width = dims.w - 4;
        const height = dims.h;
        if (this.myChart && (this.innerWidth !== width || this.innerHeight !== dims.h)) {
            this.innerWidth = width;
            this.innerHeight = this.innerHeight !== dims.h ? height - this.el.parentElement.offsetTop : this.innerHeight;
            this.myChart.resize({ width: this.innerWidth, height: this.innerHeight, silent: true });
        }
        return Promise.resolve();
    }
    async show(regexp) {
        this.myChart.dispatchAction({
            type: 'legendSelect',
            batch: this.myChart.getOption().series.filter(s => new RegExp(regexp).test(s.name)),
        });
        return Promise.resolve();
    }
    async hide(regexp) {
        this.myChart.dispatchAction({
            type: 'legendUnSelect',
            batch: this.myChart.getOption().series.filter(s => new RegExp(regexp).test(s.name)),
        });
        return Promise.resolve();
    }
    async hideById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendUnSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    async showById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryPieComponent, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        this.chartOpts = this.convert(this.result || new DataModel());
        this.setOpts();
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
        });
    }
    setOpts(notMerge = false) {
        var _a;
        if ((((_a = this.chartOpts) === null || _a === void 0 ? void 0 : _a.series) || []).length === 0) {
            this.chartOpts.title = {
                show: true,
                textStyle: { color: Utils.getLabelColor(this.el), fontSize: 20 },
                text: this.innerOptions.noDataLabel || '',
                left: 'center',
                top: 'center',
            };
            this.chartOpts.xAxis = { show: false };
            this.chartOpts.yAxis = { show: false };
            this.chartOpts.tooltip = { show: false };
        }
        else {
            this.chartOpts.title = Object.assign(Object.assign({}, this.chartOpts.title || {}), { show: false });
        }
        setTimeout(() => {
            if (this.myChart) {
                this.myChart.setOption(this.chartOpts || {}, notMerge, true);
            }
        });
    }
    getCommonSeriesParam() {
        return {
            type: 'pie',
            animation: true,
            large: true,
            clip: false,
            radius: this.type === 'pie' ? '70%' : this.type === 'rose' ? ['30%', '90%'] : ['40%', '90%'],
            roseType: this.type === 'rose' ? 'area' : undefined,
            label: {
                position: 'outer',
                alignTo: 'labelLine',
                color: Utils.getLabelColor(this.el),
            },
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
            },
        };
    }
    getCommonDataParam(color) {
        const datasetNoAlpha = this.innerOptions.datasetNoAlpha;
        return {
            lineStyle: { color },
            labelLine: {
                color: Utils.getGridColor(this.el),
            },
            itemStyle: {
                opacity: 0.8,
                borderColor: color,
                color: {
                    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: [
                        { offset: 0, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.7) },
                        { offset: 1, color: ColorLib.transparentize(color, datasetNoAlpha ? 1 : 0.3) },
                    ],
                    global: false, // false by default
                },
            },
        };
    }
    convert(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        options = Utils.mergeDeep(options || {}, data.globalParams);
        this.innerOptions = Object.assign({}, options);
        const series = [];
        let gtsList;
        if (GTSLib.isArray(data.data)) {
            data.data = GTSLib.flatDeep(data.data);
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert', 'isArray']);
            if (data.data.length > 0 && GTSLib.isGts(data.data[0])) {
                (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['convert', 'isArray 2']);
                gtsList = GTSLib.flattenGtsIdArray(data.data, 0).res;
            }
            else {
                (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['convert', 'isArray 3']);
                gtsList = data.data;
            }
        }
        else {
            (_d = this.LOG) === null || _d === void 0 ? void 0 : _d.debug(['convert', 'not array']);
            gtsList = [data.data];
        }
        (_e = this.LOG) === null || _e === void 0 ? void 0 : _e.debug(['convert'], { options: this.innerOptions, gtsList });
        const gtsCount = gtsList.length;
        const dataStruct = [];
        for (let i = 0; i < gtsCount; i++) {
            const gts = gtsList[i];
            const c = ColorLib.getColor(gts.id || i, this.innerOptions.scheme);
            const color = ((data.params || [])[i] || { datasetColor: c }).datasetColor || c;
            if (GTSLib.isGtsToPlot(gts)) {
                const values = (gts.v || []);
                const val = values[values.length - 1] || [];
                let value = 0;
                if (val.length > 0) {
                    value = val[val.length - 1];
                }
                dataStruct.push(Object.assign(Object.assign({}, this.getCommonDataParam(color)), { id: gts.id, name: ((data.params || [])[i] || { key: undefined }).key || GTSLib.serializeGtsMetadata(gts), value }));
            }
            else if (!GTSLib.isGts(gts)) {
                if (gts.hasOwnProperty('key')) {
                    dataStruct.push(Object.assign(Object.assign({}, this.getCommonDataParam(color)), { name: gts.key || '', value: gts.value || Number.MIN_VALUE }));
                }
                else {
                    Object.keys(gts).forEach((k, j) => {
                        const schemeColor = ColorLib.getColor(j, this.innerOptions.scheme);
                        const datasetColor = ((data.params || [])[i] || { datasetColor: schemeColor }).datasetColor || schemeColor;
                        dataStruct.push(Object.assign(Object.assign({}, this.getCommonDataParam(datasetColor)), { name: k, value: gts[k] }));
                    });
                }
            }
        }
        if (dataStruct.length > 0) {
            series.push(Object.assign(Object.assign({}, this.getCommonSeriesParam()), { data: dataStruct }));
        }
        (_f = this.LOG) === null || _f === void 0 ? void 0 : _f.debug(['convert', 'series'], series);
        return Object.assign({ grid: {
                left: 0, top: 0, bottom: 0, right: 0,
                containLabel: true,
            }, tooltip: {
                trigger: 'item',
                axisPointer: {
                    type: 'shadow',
                },
                backgroundColor: Utils.getCSSColor(this.el, '--warp-view-tooltip-bg-color', 'white'),
                hideDelay: this.innerOptions.tooltipDelay || 100,
            }, toolbox: {
                show: this.innerOptions.showControls,
                feature: {
                    saveAsImage: { type: 'png', excludeComponents: ['toolbox'] },
                },
            }, legend: {
                bottom: 10,
                left: 'center',
                show: false,
            }, series }, ((_h = (_g = this.innerOptions) === null || _g === void 0 ? void 0 : _g.extra) === null || _h === void 0 ? void 0 : _h.chartOpts) || {});
    }
    async export(type = 'png') {
        return Promise.resolve(this.myChart ? this.myChart.getDataURL({
            type,
            excludeComponents: ['toolbox'],
        }) : undefined);
    }
    componentDidLoad() {
        setTimeout(() => {
            this.height = Utils.getContentBounds(this.el.parentElement).h - 30;
            this.parsing = false;
            this.rendering = true;
            let initial = false;
            this.myChart = echarts.init(this.graph, null, {
                width: this.width,
                height: this.height ? this.height - 10 : undefined,
            });
            this.myChart.on('rendered', () => {
                this.rendering = false;
                if (initial) {
                    setTimeout(() => this.draw.emit());
                    initial = false;
                }
            });
            this.myChart.on('mouseover', (event) => {
                this.dataPointOver.emit({ date: event.value[0], name: event.seriesName, value: event.value[1], meta: {} });
            });
            this.el.addEventListener('mouseout', () => this.dataPointOver.emit({}));
            this.myChart.on('click', (event) => {
                this.dataPointSelected.emit({ date: event.value[0], name: event.seriesName, value: event.value[1], meta: {} });
            });
            this.myChart.setOption(this.chartOpts || {}, true, false);
            initial = true;
        });
    }
    render() {
        return h("div", { key: 'bf5bb90eb816ed43259598b27ca071c80c30e8b0', style: { width: '100%', height: '100%' } }, this.parsing ? h("discovery-spinner", null, "Parsing data...") : '', this.rendering ? h("discovery-spinner", null, "Rendering data...") : '', h("div", { key: 'ee59ae17f4f3659a88708146284fa7deecbc7512', ref: (el) => this.graph = el }));
    }
    static get is() { return "discovery-pie"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-pie.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-pie.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "string",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "unit",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "parsing": {},
            "rendering": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "show": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hideById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "showById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: \"png\" | \"svg\") => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "type",
                "methodName": "updateType"
            }, {
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-pie.js.map
